import { useCallback, useState } from 'react'

import type { AgentDirectoryPage } from '../../lib/api/admin.ts'
import { useTelemetrySource } from '../../lib/api/source.tsx'
import { agentStatePresentation } from '../../lib/agents/state.ts'
import { formatDayClock, formatMinutes } from '../../lib/format.ts'
import { agentHref } from '../../lib/logs/investigation.ts'
import { Link } from '../../lib/router.tsx'
import { useDebounced } from '../../lib/useDebounced.ts'
import { useResource } from '../../lib/useResource.ts'
import { Toolbar } from '../layout/PageHeader.tsx'
import { TenantSelect } from '../logs/Directory.tsx'
import { Badge } from '../primitives/Badge.tsx'
import { ShortId } from '../primitives/CopyValue.tsx'
import { SearchInput } from '../primitives/Field.tsx'
import { Pagination } from '../primitives/Pagination.tsx'
import { EmptyState, ErrorState, LoadingRow } from '../primitives/States.tsx'
import { TABLE, TD, TH, THEAD, TR } from '../primitives/table.ts'

const PAGE_SIZE = 50

/**
 * O diretório de agentes, com o estado da varredura de silêncio.
 *
 * Mesma tabela nas duas experiências; muda a fonte. Na Black Ghost é a VISÃO
 * GLOBAL (`GET /api/admin/agents`): todas as empresas, com seletor de empresa e
 * coluna de empresa. No cliente (`GET /api/tenant/agents`) só a própria empresa,
 * sem seletor. Vem de fotografia em memória, não do banco: `stale` marca, não
 * esconde (§2.2).
 */
export function AgentDirectoryTable() {
  const source = useTelemetrySource()
  const [page, setPage] = useState(1)
  const [tenantId, setTenantId] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const search = useDebounced(searchInput)

  const fetcher = useCallback(
    (signal: AbortSignal) =>
      source.agents(
        {
          page,
          pageSize: PAGE_SIZE,
          search,
          tenantId: source.multiTenant && tenantId !== '' ? tenantId : undefined,
        },
        signal,
      ),
    [source, page, search, tenantId],
  )
  const { data, error, loading, reload } = useResource<AgentDirectoryPage>(fetcher)

  return (
    <>
      <Toolbar>
        {source.multiTenant && (
          <TenantSelect
            value={tenantId}
            onChange={(value) => {
              setTenantId(value)
              setPage(1)
            }}
          />
        )}
        <SearchInput
          label="Buscar agente"
          placeholder={source.multiTenant ? 'hostname ou empresa' : 'hostname'}
          value={searchInput}
          onValueChange={(value) => {
            setSearchInput(value)
            setPage(1)
          }}
          className="w-64"
        />
        {data?.stale === true && (
          <Badge tone="warn" dot title={`Última fotografia às ${formatDayClock(data.captured_at)} UTC`}>
            diretório defasado
          </Badge>
        )}
        {loading && <span className="text-micro text-fg-muted">carregando…</span>}
      </Toolbar>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {error !== null ? (
          <ErrorState error={error} onRetry={reload} />
        ) : data === null ? (
          <LoadingRow label="Carregando agentes…" />
        ) : data.items.length === 0 ? (
          <EmptyState
            title="Nenhum agente encontrado."
            hint={search === '' ? 'Nenhum agente provisionado neste recorte.' : `Nada corresponde a “${search}”.`}
          />
        ) : (
          <table className={TABLE}>
            <thead className={THEAD}>
              <tr>
                <th scope="col" className={TH}>
                  Host
                </th>
                {source.multiTenant && (
                  <th scope="col" className={TH}>
                    Empresa
                  </th>
                )}
                <th scope="col" className={TH}>
                  Estado
                </th>
                <th scope="col" className={TH}>
                  Versão
                </th>
                <th scope="col" className={TH}>
                  Visto por último
                </th>
                <th scope="col" className={TH}>
                  endpoint_id
                </th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => {
                const state = agentStatePresentation(item.state)
                return (
                  <tr key={item.endpoint_id} className={TR}>
                    <th scope="row" className={`${TD} max-w-0 font-normal`}>
                      <Link
                        href={agentHref(item.endpoint_id, {})}
                        className="block truncate font-mono text-fg underline-offset-2 hover:underline"
                        title="Logs deste agente"
                      >
                        {item.hostname ?? item.endpoint_id}
                      </Link>
                    </th>
                    {source.multiTenant && (
                      <td className={`${TD} max-w-0`}>
                        <span className="block truncate">
                          {item.tenant_name ?? <span className="italic text-fg-muted">não resolvida</span>}
                        </span>
                      </td>
                    )}
                    <td className={`${TD} whitespace-nowrap`}>
                      <span className="flex items-center gap-1.5">
                        <Badge tone={state.tone} dot title={state.note}>
                          {state.label}
                        </Badge>
                        {item.silent_for_minutes !== null && (
                          <span className="tnum text-micro text-fg-muted">
                            há {formatMinutes(item.silent_for_minutes)}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className={`${TD} font-mono whitespace-nowrap`}>
                      {item.agent_version ?? <span className="text-fg-muted">—</span>}
                    </td>
                    <td className={`${TD} tnum whitespace-nowrap`}>
                      {item.last_seen_at === null ? (
                        <span className="text-fg-muted">nunca</span>
                      ) : (
                        `${formatDayClock(item.last_seen_at)} UTC`
                      )}
                    </td>
                    <td className={`${TD} whitespace-nowrap`}>
                      <ShortId id={item.endpoint_id} label="endpoint_id" />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {data !== null && data.items.length > 0 && (
        <Pagination
          page={data.page}
          pageSize={data.page_size}
          totalCount={data.total_count}
          totalPages={Math.max(1, Math.ceil(data.total_count / Math.max(1, data.page_size)))}
          busy={loading}
          onPageChange={setPage}
        />
      )}
    </>
  )
}
